import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { 
  Quotation, 
  QuotationListResponse, 
  QuotationListItem,
  QuotationLine,
  ApiResponse 
} from '@/api/types'

/**
 * Quotation management service
 * Handles quotation CRUD operations with real ERP API
 */
export class QuotationsService {
  /**
   * Get paginated list of quotations
   */
  async getAllQuotations(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<QuotationListResponse> {
    const url = endpoints.quotations.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<QuotationListResponse> | QuotationListResponse>(url)
    // Handle both wrapped and direct responses
    if ('data' in response.data && response.data.data && 'success' in response.data) {
      return (response.data as ApiResponse<QuotationListResponse>).data
    }
    return response.data as QuotationListResponse
  }

  /**
   * Get quotations by customer
   */
  async getQuotationsByCustomer(
    branchId: number,
    customerId?: number
  ): Promise<QuotationListItem[]> {
    const url = endpoints.quotations.byCustomer(branchId, customerId)
    const response = await apiClient.get<ApiResponse<QuotationListItem[]> | QuotationListItem[]>(url)
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get single quotation by customer ID
   * This API returns a single quotation object, not an array
   */
  async getQuotationByCustomerId(
    branchId: number,
    customerId: number
  ): Promise<Quotation> {
    const url = endpoints.quotations.byCustomer(branchId, customerId)
    const response = await apiClient.get<ApiResponse<Quotation> | Quotation | any>(url)
    
    let quotation: any
    if ('data' in response.data && 'success' in response.data) {
      quotation = (response.data as ApiResponse<Quotation>).data
    } else if ('quotationId' in (response.data as any)) {
      quotation = response.data
    } else if ('data' in response.data && (response.data as any).data && 'quotationId' in (response.data as any).data) {
      quotation = (response.data as any).data
    } else {
      throw new Error('Invalid response format from GetQuotationByCustomer API')
    }

    // Map API response fields to our interface fields (same normalization as getQuotationById)
    const normalizedQuotation: Quotation = {
      quotationId: quotation.quotationId,
      quotationDate: quotation.quotationDate,
      quotationNumber: quotation.quotationNumber || quotation.quotationNo, // Map quotationNo to quotationNumber
      customerId: quotation.customerId,
      dueDate: quotation.dueDate || null,
      taxRateId: quotation.taxRateId,
      subTotal: quotation.subTotal || 0,
      totalTax: quotation.totalTax || 0,
      discountPer: quotation.discountPer,
      totalDiscount: quotation.totalDiscount || 0,
      total: quotation.total || 0,
      note: quotation.note || '',
      companyId: quotation.companyId,
      branchId: quotation.branchId,
      projectId: quotation.projectId || 0,
      paymentTermId: quotation.paymentTermId || 0,
      category: quotation.category,
      bankId: quotation.bankId || 0,
      poNumber: quotation.poNumber || quotation.refNo || '', // Map refNo to poNumber if poNumber is missing
      branchInvoice: quotation.branchInvoice !== undefined ? quotation.branchInvoice : (quotation.isBranchInvoice !== undefined ? quotation.isBranchInvoice : false), // Map isBranchInvoice to branchInvoice
    }

    return normalizedQuotation
  }

  /**
   * Get single quotation by ID
   */
  async getQuotationById(branchId: number, id: number): Promise<Quotation> {
    const url = endpoints.quotations.getById(branchId, id)
    const response = await apiClient.get<ApiResponse<Quotation> | Quotation | any>(url)
    
    let quotation: any
    if ('data' in response.data && 'success' in response.data) {
      quotation = (response.data as ApiResponse<Quotation>).data
    } else {
      quotation = response.data
    }

    // Map API response fields to our interface fields
    const normalizedQuotation: Quotation = {
      quotationId: quotation.quotationId,
      quotationDate: quotation.quotationDate,
      quotationNumber: quotation.quotationNumber || quotation.quotationNo, // Map quotationNo to quotationNumber
      customerId: quotation.customerId,
      dueDate: quotation.dueDate || null,
      taxRateId: quotation.taxRateId,
      subTotal: quotation.subTotal || 0,
      totalTax: quotation.totalTax || 0,
      discountPer: quotation.discountPer,
      totalDiscount: quotation.totalDiscount || 0,
      total: quotation.total || 0,
      note: quotation.note || '',
      companyId: quotation.companyId,
      branchId: quotation.branchId,
      projectId: quotation.projectId || 0,
      paymentTermId: quotation.paymentTermId || 0,
      category: quotation.category,
      bankId: quotation.bankId || 0,
      poNumber: quotation.poNumber || quotation.refNo || '', // Map refNo to poNumber if poNumber is missing
      branchInvoice: quotation.branchInvoice !== undefined ? quotation.branchInvoice : (quotation.isBranchInvoice !== undefined ? quotation.isBranchInvoice : false), // Map isBranchInvoice to branchInvoice
    }

    return normalizedQuotation
  }

  /**
   * Get quotation lines by quotation ID
   */
  async getQuotationLinesById(branchId: number, id: number): Promise<QuotationLine[]> {
    const url = endpoints.quotations.getLinesById(branchId, id)
    const response = await apiClient.get<ApiResponse<QuotationLine[]> | QuotationLine[]>(url)
    const rawList: any[] = Array.isArray(response.data)
      ? response.data
      : 'data' in (response.data as any) && Array.isArray((response.data as any).data)
        ? ((response.data as any).data as any[])
        : []

    return rawList.map((row: any): QuotationLine => {
      const lineId = row.lineId ?? row.LineId ?? row.id ?? row.Id ?? 0
      const quotationId = row.quotationId ?? row.QuotationId ?? row.quotationID ?? row.QuotationID ?? 0
      const itemId = row.itemId ?? row.ItemId ?? 0
      const description = row.description ?? row.Description ?? ''
      const qty = Number(row.qty ?? row.Qty ?? 0) || 0
      const unitPrice = Number(row.unitPrice ?? row.UnitPrice ?? 0) || 0
      const itemPrice = Number(row.itemPrice ?? row.ItemPrice ?? 0) || 0
      const discountPercent = Number(row.discountPercent ?? row.DiscountPercent ?? 0) || 0
      const discountAmount = Number(row.discountAmount ?? row.DiscountAmount ?? 0) || 0
      const taxableAmount = Number(row.taxableAmount ?? row.TaxableAmount ?? 0) || 0
      const taxRateId = row.taxRateId ?? row.TaxRateId ?? 0
      const taxRate = Number(row.taxRate ?? row.TaxRate ?? row.taxPercent ?? row.TaxPercent ?? 0) || 0
      const taxAmount = Number(row.taxAmount ?? row.TaxAmount ?? 0) || 0
      const lineAmount = Number(row.lineAmount ?? row.LineAmount ?? 0) || 0
      const companyId = row.companyId ?? row.CompanyId
      const branchIdLine = row.branchId ?? row.BranchId
      const uom = row.uom ?? row.Uom ?? row.UOM
      const isDeleted = row.isDeleted ?? row.IsDeleted ?? 'false'
      const taxPercent = Number(row.taxPercent ?? row.TaxPercent ?? taxRate) || 0

      return {
        lineId,
        quotationId,
        itemId: Number(itemId) || 0,
        description: String(description ?? ''),
        qty,
        unitPrice,
        itemPrice,
        discountPercent,
        discountAmount,
        taxableAmount,
        taxRateId: taxRateId != null ? Number(taxRateId) : undefined,
        taxRate,
        taxAmount,
        lineAmount,
        uom: uom != null ? Number(uom) : undefined,
        companyId: companyId != null ? Number(companyId) : undefined,
        branchId: branchIdLine != null ? Number(branchIdLine) : undefined,
        taxPercent,
        isDeleted,
      }
    })
  }

  /**
   * Create new quotation
   * Maps our internal field names back to API field names
   */
  async insertQuotation(quotation: Quotation): Promise<Quotation> {
    // Convert our internal format to API format (same as update)
    const apiPayload: any = {
      quotationId: quotation.quotationId || 0,
      quotationDate: quotation.quotationDate,
      quotationNo: quotation.quotationNumber || '', // API expects quotationNo
      customerId: quotation.customerId,
      dueDate: quotation.dueDate || null,
      taxRateId: quotation.taxRateId || 0,
      subTotal: quotation.subTotal || 0,
      totalTax: quotation.totalTax || 0,
      discountPer: quotation.discountPer || 0,
      totalDiscount: quotation.totalDiscount || 0,
      total: quotation.total || 0,
      note: quotation.note || '',
      companyId: quotation.companyId || 0,
      branchId: quotation.branchId || 0,
      projectId: quotation.projectId || 0,
      paymentTermId: quotation.paymentTermId || 0,
      category: quotation.category || null,
      bankId: quotation.bankId || 0,
      poNumber: quotation.poNumber || '',
      isBranchInvoice: quotation.branchInvoice !== undefined ? quotation.branchInvoice : false, // API expects isBranchInvoice
      ipAddress: quotation.ipAddress || '', // Set logged-in user's IP address
      retentionPercent: 0, // Default value if not provided
      totalRetentionAmount: 0, // Default value if not provided
      amountType: 0, // Default value if not provided
      // IMPORTANT: UI supports manual item entry without selecting inventory item.
      // In that case itemId stays 0; do NOT drop those lines.
      quotationD:
        quotation.quotationD
          ?.filter((line) => {
            const isDeleted = line.isDeleted === true || line.isDeleted === 'true'
            if (isDeleted) return true
            const hasDesc = Boolean(line.description && String(line.description).trim())
            const hasQty = Number(line.qty ?? 0) > 0
            const hasVat = Number(line.taxRateId ?? 0) > 0
            return hasDesc && hasQty && hasVat
          })
          .map((line) => {
            const isDeleted = line.isDeleted === true || line.isDeleted === 'true'
            const taxRateValue = Number(line.taxRate ?? line.taxPercent ?? 0) || 0
            return {
              lineId: line.lineId || 0,
              quotationId: line.quotationId || quotation.quotationId || 0,
              itemId: Number(line.itemId ?? 0) || 0,
              description: line.description || '',
              qty: Number(line.qty ?? 0) || 0,
              unitPrice: Number(line.unitPrice ?? 0) || 0,
              itemPrice: Number(line.itemPrice ?? 0) || 0,
              discountPercent: Number(line.discountPercent ?? 0) || 0,
              discountAmount: Number(line.discountAmount ?? 0) || 0,
              taxableAmount: Number(line.taxableAmount ?? 0) || 0,
              taxRateId: Number(line.taxRateId ?? 0) || 0,
              taxRate: taxRateValue,
              taxAmount: Number(line.taxAmount ?? 0) || 0,
              lineAmount: Number(line.lineAmount ?? 0) || 0,
              uom: line.uom ?? null,
              companyId: line.companyId || quotation.companyId || 0,
              branchId: line.branchId || quotation.branchId || 0,
              taxPercent: Number(line.taxPercent ?? taxRateValue) || 0,
              isDeleted: isDeleted ? 'true' : 'false',
            }
          }) || [],
    }

    console.log('========== API PAYLOAD (Insert) ==========')
    console.log('Converted Payload:', JSON.stringify(apiPayload, null, 2))
    console.log('==========================================')

    try {
      const response = await apiClient.post<ApiResponse<Quotation> | Quotation>(
        endpoints.quotations.insert,
        apiPayload
      )
      console.log('========== API RESPONSE (Insert) ==========')
      console.log('Raw response:', response)
      console.log('Response data:', response.data)
      console.log('==========================================')
      if ('data' in response.data && 'success' in response.data) {
        return (response.data as ApiResponse<Quotation>).data
      }
      return response.data as Quotation
    } catch (error: any) {
      console.error('========== INSERT ERROR DETAILS ==========')
      console.error('Error:', error)
      console.error('Error Response:', error?.response)
      console.error('Error Response Data:', error?.response?.data)
      console.error('Error Response Status:', error?.response?.status)
      console.error('Error Response Headers:', error?.response?.headers)
      console.error('Payload that caused error:', JSON.stringify(apiPayload, null, 2))
      console.error('==========================================')
      throw error
    }
  }

  /**
   * Update existing quotation
   * Maps our internal field names back to API field names
   */
  async updateQuotation(quotation: Quotation): Promise<Quotation> {
    // Convert our internal format to API format
    // Include all lines (deleted and non-deleted) as API needs to know which ones to delete
    const allLines = (quotation.quotationD || []).filter((line) => {
      // Skip empty lines that are not deleted
      const isDeleted = line.isDeleted === true || line.isDeleted === 'true'
      if (isDeleted) return true
      const hasDesc = Boolean(line.description && String(line.description).trim())
      const hasQty = Number(line.qty ?? 0) > 0
      const hasVat = Number(line.taxRateId ?? 0) > 0
      return hasDesc && hasQty && hasVat
    })

    const apiPayload: any = {
      quotationId: quotation.quotationId || 0,
      quotationDate: quotation.quotationDate,
      quotationNo: quotation.quotationNumber || '', // API expects quotationNo
      customerId: quotation.customerId,
      dueDate: quotation.dueDate || null,
      taxRateId: quotation.taxRateId || 0,
      subTotal: quotation.subTotal || 0,
      totalTax: quotation.totalTax || 0,
      discountPer: quotation.discountPer || 0,
      totalDiscount: quotation.totalDiscount || 0,
      total: quotation.total || 0,
      note: quotation.note || '',
      companyId: quotation.companyId || 0,
      branchId: quotation.branchId || 0,
      projectId: quotation.projectId || 0,
      paymentTermId: quotation.paymentTermId || 0,
      category: quotation.category ?? null,
      bankId: quotation.bankId || 0,
      poNumber: quotation.poNumber || '',
      isBranchInvoice: quotation.branchInvoice !== undefined ? quotation.branchInvoice : false, // API expects isBranchInvoice
      ipAddress: quotation.ipAddress || '', // Set logged-in user's IP address
      retentionPercent: 0, // Default value if not provided
      totalRetentionAmount: 0, // Default value if not provided
      amountType: 0, // Default value if not provided
      quotationD: allLines.map(line => {
        const isDeleted = line.isDeleted === true || line.isDeleted === 'true'
        // Use taxPercent if taxRate is 0 or not set
        const taxRateValue = line.taxRate || line.taxPercent || 0
        
        return {
          lineId: line.lineId || 0,
          quotationId: quotation.quotationId || 0,
          itemId: line.itemId || 0,
          description: line.description || '',
          qty: line.qty || 0,
          unitPrice: line.unitPrice || 0,
          itemPrice: line.itemPrice || 0,
          discountPercent: line.discountPercent || 0,
          discountAmount: line.discountAmount || 0,
          taxableAmount: line.taxableAmount || 0,
          taxRateId: line.taxRateId || 0,
          taxRate: taxRateValue, // Use actual tax rate percentage, not 0
          taxAmount: line.taxAmount || 0,
          lineAmount: line.lineAmount || 0,
          uom: line.uom ?? null,
          companyId: line.companyId || quotation.companyId || 0,
          branchId: line.branchId || quotation.branchId || 0,
          taxPercent: line.taxPercent || taxRateValue || 0,
          isDeleted: isDeleted ? 'true' : 'false', // Lowercase "true" or "false" (small letters)
        }
      }),
    }

    // Remove null/undefined values that might cause issues
    Object.keys(apiPayload).forEach(key => {
      if (apiPayload[key] === undefined) {
        delete apiPayload[key]
      }
    })

    console.log('========== API PAYLOAD (Update) ==========')
    console.log('Converted Payload:', JSON.stringify(apiPayload, null, 2))
    console.log('Payload Keys:', Object.keys(apiPayload))
    console.log('Lines Count:', apiPayload.quotationD?.length || 0)
    console.log('==========================================')

    try {
      const response = await apiClient.put<ApiResponse<Quotation> | Quotation>(
        endpoints.quotations.update,
        apiPayload
      )
      console.log('========== API RESPONSE (Update) ==========')
      console.log('Raw response:', response)
      console.log('Response data:', response.data)
      console.log('==========================================')
      if ('data' in response.data && 'success' in response.data) {
        return (response.data as ApiResponse<Quotation>).data
      }
      return response.data as Quotation
    } catch (error: any) {
      console.error('========== UPDATE ERROR DETAILS ==========')
      console.error('Error:', error)
      console.error('Error Response:', error.response)
      console.error('Error Response Data:', error.response?.data)
      console.error('Error Response Status:', error.response?.status)
      console.error('Error Response Headers:', error.response?.headers)
      console.error('Payload that caused error:', JSON.stringify(apiPayload, null, 2))
      console.error('==========================================')
      throw error
    }
  }

  /**
   * Get user IP address (logged-in user's IP, not server's IP)
   */
  private async getUserIPAddress(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  /**
   * Convert quotation to invoice
   */
  async convertToInvoice(id: number, ipAddress?: string): Promise<{ result: { isError: boolean; msg: string; id: number } }> {
    // Get IP address if not provided (this gets the logged-in user's IP, not server IP)
    const userIP = ipAddress || await this.getUserIPAddress()
    const url = endpoints.quotations.convertToInvoice(id, userIP)
    
    console.log('%c=== QuotationsService.convertToInvoice ===', 'background: #9C27B0; color: white; padding: 5px; font-weight: bold; font-size: 14px;')
    console.log('%cQuotation ID:', 'color: #FF9800; font-weight: bold;', id)
    console.log('%cIP Address (User):', 'color: #4CAF50; font-weight: bold;', userIP)
    console.log('%cEndpoint:', 'color: #2196F3; font-weight: bold;', url)
    console.log('%c=========================================', 'background: #9C27B0; color: white; padding: 5px; font-weight: bold;')
    
    const response = await apiClient.post<ApiResponse<{ result: { isError: boolean; msg: string; id: number } }> | { result: { isError: boolean; msg: string; id: number } }>(url)
    
    if ('data' in response.data && 'success' in response.data) {
      return (response.data as ApiResponse<{ result: { isError: boolean; msg: string; id: number } }>).data
    }
    return response.data as { result: { isError: boolean; msg: string; id: number } }
  }
}

export const quotationsService = new QuotationsService()
